import type { TextRun } from '@/api/types/stowed-element'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { parseStyledRunsFromHtml } from '@/lib/shapes/shape-text-clipboard'
import { extractRunStyle } from '@/lib/text/run-style'
import { parseExternalClipboardHtml } from '@/lib/text/parse-external-clipboard-html'
import { expandTabsToSpaces } from '@/lib/text/paste-sanitize'
import type { ShapeTextFonts } from '../types/shape-text-layout-state'

export type StyledPasteRun = { textContent: string; style: Partial<TextRun> }

/**
 * The run-level fields of a pending caret style, safe to spread onto a `TextRun`.
 *
 * `alignment` is a *paragraph* property and `mixed*`/`fontIdByFontId` are output-only display flags
 * that must never be written onto a run — the deleted contentEditable path stripped `alignment` for
 * exactly this reason (WMPCM-7004). Kept as an explicit allowlist rather than an omit-list so a new
 * output-only field added to `TextRunStyleUpdate` cannot silently start leaking into pasted runs.
 */
function pendingRunFields(style: TextRunStyleUpdate): Partial<TextRun> {
  const out: Partial<TextRun> = {}

  if (style.fontFamilyId !== undefined) out.fontFamilyId = style.fontFamilyId
  if (style.fontId !== undefined) out.fontId = style.fontId
  if (style.pointSize !== undefined) out.pointSize = style.pointSize
  if (style.underline !== undefined) out.underline = style.underline
  if (style.strikethrough !== undefined) out.strikethrough = style.strikethrough
  if (style.kerning !== undefined) out.kerning = style.kerning
  if (style.tracking !== undefined) out.tracking = style.tracking
  if (style.leading !== undefined) out.leading = style.leading

  return out
}

/**
 * Styled runs from a clipboard `text/html` payload: larry's own format parses directly, anything
 * else (Word, Docs, a web page) is converted to it first. `unresolvedFamilies` names the source
 * typefaces this account cannot use, for the caller to report.
 */
export function styledRunsFromClipboardHtml(
  html: string,
  {
    caretRun,
    pendingStyle,
    fonts,
  }: {
    caretRun: TextRun | null
    pendingStyle: TextRunStyleUpdate | null
    fonts: ShapeTextFonts
  }
): { styledRuns: StyledPasteRun[] | null; unresolvedFamilies: Set<string> } {
  let styledRuns: StyledPasteRun[] | null = parseStyledRunsFromHtml(html)
  // Families the clipboard asked for that this account cannot use. Collected across the
  // whole paste and reported once (WMPCM-9199) — the caret-font fallback stays, but it
  // stops being silent.
  //
  // Still reported now that the family is always forced: `resolveRun` resolves the
  // source family before overriding it, precisely so this signal survives. The toast
  // therefore now fires only for a genuinely unavailable typeface, not for every paste —
  // which is the useful half. Whether AC2 makes the message itself redundant (the font
  // is overridden either way) is a product call; the wiring is deliberately kept live so
  // that decision stays reversible rather than silently pre-made by deleting it.
  const unresolvedFamilies = new Set<string>()

  if (!styledRuns) {
    // External HTML: convert to internal data-text-run format then parse
    const defaults = extractRunStyle(undefined)
    const caretStyle: Record<string, number> = {}

    if (caretRun?.fontId != null) caretStyle.fontId = caretRun.fontId
    if (caretRun?.fontFamilyId != null) caretStyle.fontFamilyId = caretRun.fontFamilyId
    if (caretRun?.pointSize != null) caretStyle.pointSize = caretRun.pointSize
    const baseStyle = { ...defaults, ...caretStyle }
    // External text keeps its formatting — underline, strikethrough, superscript/subscript and
    // paragraph structure come from Word/Docs/the web page — but the font family, font style
    // (bold/italic) and point size come from the destination (WMPCM-7028 AC2, WMPCM-13346), so
    // a bold 24pt heading pastes in the destination's font and size, not bold and not 24pt.
    //
    // "Destination" resolves to the style the next typed character would use: the pending
    // style when the user has chosen one at this caret (Adobe-style typing attributes), the
    // caret's own run otherwise (WMPCM-7004).
    const destinationFamilyId = pendingStyle?.fontFamilyId ?? baseStyle.fontFamilyId
    // The caret's font belongs to the caret's family, so it only applies when the caret run
    // actually carries one and a pending style has not switched families. `baseStyle.fontId` alone
    // is not enough: without a caret `fontId` it is the default family's font, which would pair
    // with the caret's family. Otherwise take the destination family's regular style.
    const caretFontApplies =
      caretRun?.fontId != null && destinationFamilyId === baseStyle.fontFamilyId
    const destinationFontId =
      pendingStyle?.fontId ??
      (caretFontApplies
        ? baseStyle.fontId
        : destinationFamilyId != null
          ? fonts?.getFontIdByVariant?.(destinationFamilyId, false, false)
          : undefined)
    const destinationPointSize = pendingStyle?.pointSize ?? baseStyle.pointSize
    const internalHtml = parseExternalClipboardHtml(html, baseStyle, fonts ?? undefined, {
      forceFontFamilyId: destinationFamilyId ?? undefined,
      forceFontId: destinationFontId ?? undefined,
      forcePointSize: destinationPointSize ?? undefined,
      onUnresolvedFamily: (name) => unresolvedFamilies.add(name),
    })

    if (internalHtml) styledRuns = parseStyledRunsFromHtml(internalHtml)
  }

  return { styledRuns, unresolvedFamilies }
}

/**
 * Prepares styled runs for insertion.
 *
 * Same AC7 rule the plain-text path applies, and it matters here too now that a
 * styled paste can originate from external HTML: a `\r` reaching the paragraph model puts
 * every flat-offset walk that counts it out of step with the layout's caret walk.
 *
 * The pending run style fills only what the clipboard does NOT specify. That precedence is
 * deliberate and matches the deleted contentEditable path (WMPCM-7004), where the pending
 * style formed the *base* passed to `parseExternalClipboardHtml` and the clipboard's own
 * underline/strikethrough were then preserved on top of it. External runs already carry the
 * destination's font and size (`styledRunsFromClipboardHtml` resolves them from the same pending
 * style), so for them only the fields the parser leaves unset are filled here.
 */
export function finalizeStyledPasteRuns(
  styledRuns: StyledPasteRun[],
  pendingStyle: TextRunStyleUpdate | null
): StyledPasteRun[] {
  return styledRuns.map((run) => ({
    ...run,
    textContent: run.textContent.replace(/\r/g, ''),
    style: pendingStyle ? { ...pendingRunFields(pendingStyle), ...run.style } : run.style,
  }))
}

/** Plain clipboard text made safe for the shape-text model. */
export function sanitizePlainPaste(plain: string): string {
  // AC7: strip carriage returns
  let plainText = plain.replace(/\r/g, '')

  // WMPCM-10141: expand tabs to spaces before the range filter below — a tab (U+0009) is
  // well within range and would otherwise pass through untouched, then silently collapse
  // to a single space's width when measured/rendered (no tab-stop support in this
  // codebase's canvas text measurement — see `expandTabsToSpaces`'s doc comment).
  plainText = expandTabsToSpaces(plainText)

  // AC6: replace characters outside U+0000–U+5FFF with spaces
  plainText = [...plainText].map((ch) => ((ch.codePointAt(0) ?? 0) > 0x5fff ? ' ' : ch)).join('')

  return plainText
}
